import * as stylex from "@stylexjs/stylex";
import { Check, LoaderCircle, Power, RefreshCw, RotateCw, TriangleAlert } from "lucide-react";
import { copy } from "@/copy";
import { isNewerVersion } from "@/domain/build";
import "@/theme/seasons.stylex";
import { season } from "@/theme/seasons.stylex";
import "@/theme/tokens.stylex";
import { colors, radii, shadows, space } from "@/theme/tokens.stylex";
import "@/theme/type.stylex";
import { type } from "@/theme/type.stylex";
import { Button } from "@/ui/Button";
import { Eyebrow } from "@/ui/Eyebrow";
import { Icon } from "@/ui/Icon";
import type { RefreshData } from "../refreshData";
import type { ReloadAppRow } from "../useReloadApp";
type DataAndAppCardProps = {
  online: boolean;
  refresh: RefreshData;
  updatedMinutes?: number;
  onRefresh: () => void;
  reloadApp: ReloadAppRow;
};
const text = copy.settings.dataAndApp;
const styles = {
  ring: {
    kGVxlE: "xnfxhx2",
    $$css: true
  }
};
export function DataAndAppCard({
  online,
  refresh,
  updatedMinutes,
  onRefresh,
  reloadApp
}: DataAndAppCardProps) {
  const busy = refresh.kind === "busy";
  return <section aria-label={text.title} className="x78zum5 xdt5ytf x1c1vhfx xh540kj xwe7p7i xnfxhx2 xgkd4oa x9f619 x1a11ngh">
      <Eyebrow>{text.title}</Eyebrow>
      <div className="x78zum5 x1a02dak x6s0dn4 x1qughib x9snybx">
        <div className="x78zum5 xdt5ytf x17d4w8g xeuugli xk30mkq">
          <span className="x19d36u7 xfbjfs5 x1j7pf75">{text.refresh.title}</span>
          <span className="x1jvydc1 x15i68ds x37zpob x1fzhlzt">{text.refresh.body}</span>
          <span role="status" aria-live="polite" className="x78zum5 x6s0dn4 x152posx xvrgn94 xr0iwhn xif65rj xkv8mnm xtvhhri xwn7fz2">
            {online && refresh.kind === "fail" && <span className="x78zum5 x2lah0s x3258oq">
                <Icon icon={TriangleAlert} size={18} />
              </span>}
            {online && refresh.kind === "done" && <span className="x78zum5 x2lah0s">
                <Icon icon={Check} size={18} />
              </span>}
            {refreshStatus(online, refresh, updatedMinutes)}
          </span>
        </div>
        <Button variant="secondary" icon={busy ? LoaderCircle : RefreshCw} disabled={!online || busy} onClick={onRefresh} xstyle={styles.ring}>
          {busy ? text.refresh.busyButton : text.refresh.button}
        </Button>
      </div>
      <ReloadRow online={online} {...reloadApp} />
    </section>;
}
function ReloadRow({
  online,
  current,
  next,
  reload,
  onAsk,
  onCancel,
  onReload
}: ReloadAppRow & {
  online: boolean;
}) {
  const ready = next !== undefined && isNewerVersion(next, current);
  const target = ready ? next : current;
  return <div className="x78zum5 x1a02dak x6s0dn4 x1qughib x9snybx x7beud5 x1jidhvw">
      <div className="x78zum5 xdt5ytf x17d4w8g xeuugli xk30mkq">
        <span className="x19d36u7 xfbjfs5 x1j7pf75">{text.reload.title}</span>
        <span className="x1jvydc1 x15i68ds x37zpob x1fzhlzt">{text.reload.body}</span>
        <span role="status" aria-live="polite" className="x78zum5 x6s0dn4 x152posx xvrgn94 xr0iwhn xif65rj xkv8mnm xtvhhri xwn7fz2">
          {online && ready && <span className="x1fsd2vl x170jfvy x2lah0s xqhrb7l x7ns719" />}
          {reloadStatus(online, ready, target, current)}
        </span>
      </div>
      {reload.kind === "asking" ? <div className="x78zum5 x1a02dak x6s0dn4 x1qughib x1hu4l6k x1gebrk9 xeuugli x5r8ks9 x1fv5t7x x10g99xj x9f619">
          <span className="x1jvydc1 x15i68ds x37zpob x1fzhlzt x1iv9x3n">{text.reload.confirm}</span>
          <div className="x78zum5 x1a02dak x152posx">
            <Button variant="ghost" onClick={onCancel}>
              {text.reload.cancel}
            </Button>
            <Button variant="primary" icon={RotateCw} onClick={onReload}>
              {text.reload.reloadNow}
            </Button>
          </div>
        </div> : <Button variant={ready ? "primary" : "secondary"} icon={Power} disabled={!online} onClick={onAsk} xstyle={!ready && styles.ring}>
          {text.reload.button}
        </Button>}
      {reload.kind === "reloading" && <ReloadingCover version={target} />}
    </div>;
}
function ReloadingCover({
  version
}: {
  version: string;
}) {
  return <div role="alert" aria-busy="true" className="xixxii4 x10a8y8t x1aciyhk xrvj5dj x1ku5rj1 x18ppelm x9f619 xh540kj">
      <div className="x78zum5 xdt5ytf x6s0dn4 x1ur044a x2b8uid x1lr1uin">
        <span className="xrvj5dj x1ku5rj1 x1dmp6jm xwzfr38 xqhrb7l x1ubcl0s">
          <Icon icon={RotateCw} size={34} strokeWidth={1.5} />
        </span>
        <div className="x78zum5 xdt5ytf x6s0dn4 x1hu4l6k">
          <div className="xuaujqh x1v94iar xipi2l9 x1159mfc">{text.reload.coverTitle}</div>
          <span className="x19d36u7 x15i68ds xfrs9s4 x1fzhlzt">{text.reload.coverBody}</span>
        </div>
        <Eyebrow>{text.reload.version(version)}</Eyebrow>
      </div>
    </div>;
}
function reloadStatus(online: boolean, ready: boolean, target: string, current: string) {
  if (!online) return text.reload.offline;
  return ready ? text.reload.ready(target, current) : text.reload.upToDate(current);
}
function refreshStatus(online: boolean, refresh: RefreshData, minutes: number | undefined) {
  if (!online) return text.refresh.offline(minutes);
  if (refresh.kind === "busy") return text.refresh.busy;
  if (refresh.kind === "fail") return text.refresh.failed;
  return minutes === undefined ? null : text.refresh.updated(minutes);
}